import { useApolloClient } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMarkChatAsRead } from '../../../hooks';
import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import { GET_CHATS_BY_FILTERS, type ChatAppType } from '../../../queries/chats';
import { browserNotificationManager } from '../../../services/browser-notification-manager';
import { isTabAndWindowVisible, shouldShowBrowserNotification } from '../lib/notification-decision';
import { buildNotificationText, normalizeChatUuid, updateChatsListAfterStream } from '../lib/notification-helpers';

interface StreamRegistration {
  chatUuid: string;
  isChatVisible: boolean;
  isStandaloneMode: boolean;
}

/**
 * How often to refetch the chats list while the server reports at least one
 * active stream. Background stream completion is detected as the
 * activeStreamId → null transition between refetches.
 */
const ACTIVE_STREAM_POLL_INTERVAL_MS = 10_000;

export interface ChatNotificationController {
  // State
  hasNotifications: boolean;
  browserNotificationsEnabled: boolean;
  browserNotificationPermission: NotificationPermission;
  browserNotificationsSupported: boolean;

  // Actions
  setHasNotifications: (has: boolean) => void;
  enableBrowserNotifications(): Promise<boolean>;
  disableBrowserNotifications(): void;
  clearNotifications(): void;

  // Stream lifecycle
  registerStream(chatUuid: string, isChatVisible: boolean, isStandaloneMode: boolean): void;
  unregisterStream(chatUuid: string): void;
  onStreamFinish(params: {
    chatUuid: string;
    isChatVisible?: boolean;
    isStandaloneMode?: boolean;
    currentChatUuid?: string;
  }): Promise<void>;
}

export function useChatNotificationController({
  userUuid,
  currentChatUuid,
  effectiveAppType,
  serverSettings,
  onPersist,
  isPublicMode = false,
}: {
  userUuid?: string;
  currentChatUuid?: string;
  effectiveAppType?: ChatAppType | null;
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
  isPublicMode?: boolean;
}): ChatNotificationController {
  const apolloClient = useApolloClient();
  const isNovaNotificationsEnabled = useFeatureFlag('nova-notifications', false);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(false);
  const isInitialized = useRef(false);
  // Local stream registrations, keyed by normalized chat uuid. A registration
  // means this client submitted a request the server may not have accounted
  // for yet (no chat row / no activeStreamId), so polling must keep running
  // until the server reports the stream or its completion.
  const streamRegistrations = useRef<Map<string, StreamRegistration>>(new Map());
  const [registeredStreamCount, setRegisteredStreamCount] = useState(0);
  const markChatAsRead = useMarkChatAsRead(userUuid, effectiveAppType);

  // Query chats to check for unread messages and active background streams
  const { data, startPolling, stopPolling } = useQuery(GET_CHATS_BY_FILTERS, {
    variables: {
      userUuid,
      appType: effectiveAppType,
    },
    skip: isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const { chatsList: chats = [] } = data ?? {};

  // Poll the chats list while the server reports any active stream — or while
  // a locally submitted request is not yet accounted for by the server — so
  // the sidebar reflects background progress and completions without keeping
  // a hidden chat instance per stream.
  const hasActiveStreams = chats.some((chat) => chat.activeStreamId);
  const shouldPoll = hasActiveStreams || registeredStreamCount > 0;
  useEffect(() => {
    if (isPublicMode || !shouldPoll) {
      return undefined;
    }

    startPolling(ACTIVE_STREAM_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [shouldPoll, isPublicMode, startPolling, stopPolling]);

  // Initialize browser notification state from server settings or local state
  useEffect(() => {
    if (userUuid && !isInitialized.current) {
      isInitialized.current = true;
      const enabled = serverSettings?.notificationsEnabled ?? browserNotificationManager.isEnabled();
      setBrowserNotificationsEnabled(enabled);
      browserNotificationManager.setFeatureFlagEnabled(isNovaNotificationsEnabled);
    }
  }, [userUuid, isNovaNotificationsEnabled, serverSettings]);

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setBrowserNotificationsEnabled(serverSettings.notificationsEnabled);
    }
  }, [serverSettings]);

  // Check for unread messages when chats data changes
  useEffect(() => {
    const hasUnreadMessages = chats.some((chat) => chat.hasUnreadMessages);
    setHasNotifications(hasUnreadMessages);
  }, [chats]);

  // Browser notification actions
  const enableBrowserNotifications = useCallback(async (): Promise<boolean> => {
    try {
      const permission = await browserNotificationManager.requestPermission();
      if (permission === 'granted') {
        browserNotificationManager.enable();
        setBrowserNotificationsEnabled(true);
        onPersist?.('notificationsEnabled', true);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [onPersist]);

  const disableBrowserNotifications = useCallback(() => {
    browserNotificationManager.disable();
    setBrowserNotificationsEnabled(false);
    onPersist?.('notificationsEnabled', false);
  }, [onPersist]);

  const clearNotifications = useCallback(() => {
    browserNotificationManager.clearNotifications();
  }, []);

  // Stream lifecycle
  const registerStream = useCallback((chatUuid: string, isChatVisible: boolean, isStandaloneMode: boolean) => {
    streamRegistrations.current.set(normalizeChatUuid(chatUuid), {
      chatUuid,
      isChatVisible,
      isStandaloneMode,
    });
    setRegisteredStreamCount(streamRegistrations.current.size);

    browserLogger.debug('[Notification Controller] Registered stream', {
      chatUuid,
      isChatVisible,
      isStandaloneMode,
      activeStreams: streamRegistrations.current.size,
    });
  }, []);

  const unregisterStream = useCallback((chatUuid: string) => {
    const existed = streamRegistrations.current.delete(normalizeChatUuid(chatUuid));
    setRegisteredStreamCount(streamRegistrations.current.size);

    if (existed) {
      browserLogger.debug('[Notification Controller] Unregistered stream', {
        chatUuid,
        activeStreams: streamRegistrations.current.size,
      });
    }
  }, []);

  const onStreamFinish = useCallback(
    async ({
      chatUuid,
      isChatVisible: passedIsChatVisible,
      isStandaloneMode: passedIsStandaloneMode,
      currentChatUuid: passedCurrentChatUuid,
    }: {
      chatUuid: string;
      isChatVisible?: boolean;
      isStandaloneMode?: boolean;
      currentChatUuid?: string;
    }) => {
      if (isPublicMode) {
        unregisterStream(chatUuid);
        return;
      }

      const registration = streamRegistrations.current.get(normalizeChatUuid(chatUuid));
      if (!registration) {
        browserLogger.debug('[Notification Controller] Stream finished but no registration found', { chatUuid });
      }

      // Use stored values from registration time, with fallback to passed values
      const effectiveIsChatVisible = passedIsChatVisible ?? registration?.isChatVisible ?? false;
      const effectiveIsStandaloneMode = passedIsStandaloneMode ?? registration?.isStandaloneMode ?? false;
      const effectiveCurrentChatUuid = passedCurrentChatUuid ?? currentChatUuid;
      const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);
      const isOptimisticNewChat = chatUuid.startsWith('new-chat-');
      const isCurrentChat =
        effectiveCurrentChatUuid !== undefined && normalizeChatUuid(effectiveCurrentChatUuid) === chatUuidWithoutPrefix;
      const isVisibleCurrentChat = isCurrentChat && effectiveIsChatVisible;

      // Update Apollo cache
      updateChatsListAfterStream({
        apolloClient,
        chatUuid,
        userUuid: userUuid ?? '',
        appType: effectiveAppType,
        hasUnreadMessages: !isVisibleCurrentChat,
      });

      // For optimistic first-turn chats, the stream endpoint owns creation. Avoid read-side
      // mutations until a persisted chat row is guaranteed to exist.
      if (!isOptimisticNewChat && isVisibleCurrentChat) {
        markChatAsRead(chatUuidWithoutPrefix);
      }

      const tabAndWindowVisible = isTabAndWindowVisible();

      // Determine if we should show browser notification
      const shouldShow = shouldShowBrowserNotification({
        isChatVisible: effectiveIsChatVisible,
        isStandaloneMode: effectiveIsStandaloneMode,
        isCurrentChat,
        isOrphanedStream: false,
      });

      const notificationText = shouldShow
        ? await buildNotificationText({
            apolloClient,
            chatUuid,
          })
        : 'You have a new message';

      // Show browser notification if enabled and conditions are met
      if ((browserNotificationManager.isEnabled() && shouldShow) || !tabAndWindowVisible) {
        // Calculate forceShow: show notification even if tab/window is visible when:
        // - Chat is minimized (user can't see it)
        // - In standalone mode (user might have switched chats)
        const forceShow = !effectiveIsChatVisible || effectiveIsStandaloneMode;

        browserLogger.debug('[Notification Controller] Showing browser notification', {
          chatUuid,
          notificationText,
          forceShow,
          tabAndWindowVisible,
          effectiveIsChatVisible,
          effectiveIsStandaloneMode,
        });

        browserNotificationManager.showChatNotification(notificationText, chatUuid);
      } else {
        browserLogger.debug('[Notification Controller] Skipping browser notification', {
          chatUuid,
          shouldShow,
          isEnabled: browserNotificationManager.isEnabled(),
          effectiveIsChatVisible,
          effectiveIsStandaloneMode,
          isCurrentChat,
        });
      }

      // Update notification badge state
      setHasNotifications(true);

      // Clean up registration
      unregisterStream(chatUuid);
    },
    [apolloClient, userUuid, currentChatUuid, markChatAsRead, unregisterStream, effectiveAppType, isPublicMode],
  );

  // Detect background stream completions by diffing the server-reported
  // activeStreamId between chats list refetches. The current chat's stream
  // lifecycle is owned by the local chat instance (onStreamFinish above);
  // this only covers chats streaming somewhere else (other chat, other tab,
  // before a reload).
  const previousActiveStreamChatUuidsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const previousActiveStreamChatUuids = previousActiveStreamChatUuidsRef.current;
    const activeStreamChatUuids = new Set(
      chats.filter((chat) => chat.uuid && chat.activeStreamId).map((chat) => normalizeChatUuid(chat.uuid!)),
    );
    previousActiveStreamChatUuidsRef.current = activeStreamChatUuids;

    // The first snapshot has nothing to diff against; completions that
    // happened while no client was around are surfaced through the
    // server-side unread state instead.
    if (isPublicMode || !previousActiveStreamChatUuids) {
      return;
    }

    const currentChatUuidWithoutPrefix = currentChatUuid ? normalizeChatUuid(currentChatUuid) : undefined;

    // Hand off local registrations once the server accounts for them: a row
    // with an activeStreamId sustains polling by itself, and a row that
    // finished in the background no longer needs tracking. Rows the server
    // does not know about yet keep their registration (and the polling).
    for (const registeredChatUuid of [...streamRegistrations.current.keys()]) {
      const row = chats.find((chat) => normalizeChatUuid(chat.uuid ?? '') === registeredChatUuid);
      if (row && (row.activeStreamId || row.hasUnreadMessages)) {
        unregisterStream(registeredChatUuid);
      }
    }

    for (const chatUuid of previousActiveStreamChatUuids) {
      if (activeStreamChatUuids.has(chatUuid) || chatUuid === currentChatUuidWithoutPrefix) {
        continue;
      }

      const completedChat = chats.find((chat) => normalizeChatUuid(chat.uuid ?? '') === chatUuid);
      if (!completedChat?.hasUnreadMessages) {
        continue;
      }

      browserLogger.info('[Notification Controller] Detected background stream completion', { chatUuid });

      void buildNotificationText({ apolloClient, chatUuid }).then((notificationText) => {
        if (browserNotificationManager.isEnabled()) {
          browserNotificationManager.showChatNotification(notificationText, chatUuid);
        }
      });
    }
  }, [apolloClient, chats, currentChatUuid, isPublicMode, unregisterStream]);

  return useMemo(
    () => ({
      hasNotifications,
      browserNotificationsEnabled,
      browserNotificationPermission: browserNotificationManager.getPermission(),
      browserNotificationsSupported: browserNotificationManager.isSupported(),
      setHasNotifications,
      enableBrowserNotifications,
      disableBrowserNotifications,
      clearNotifications,
      registerStream,
      unregisterStream,
      onStreamFinish,
    }),
    [
      hasNotifications,
      browserNotificationsEnabled,
      setHasNotifications,
      enableBrowserNotifications,
      disableBrowserNotifications,
      clearNotifications,
      registerStream,
      unregisterStream,
      onStreamFinish,
    ],
  );
}
